import React from 'react';
import { Plus, Trash2, Star } from 'lucide-react';
import { useTheme } from '../../../../core/ui/ThemeContext';
import { useLanguage } from '../../../../core/i18n/LanguageContext';
import { getGlassStyles } from './ProductManagementStyles';

// Leerzeile fuer neue Bezugsquelle — alle Felder mit Defaults.
const EMPTY_SOURCE = {
  vendor: '',
  vendor_name: '',
  vendor_material_no: '',
  info_record_no: '',
  purchasing_org: '',
  purchasing_group: '',
  net_price: '',
  currency: 'EUR',
  price_unit: 1,
  order_unit: '',
  min_order_qty: 1,
  planned_delivery_time: '',
  incoterms: '',
  is_primary: false,
  scales: [],
};

// Leerzeile fuer eine Preisstaffel.
const EMPTY_SCALE = { min_qty: '', amount: '', currency: 'EUR', price_unit: 1 };

// Kompakter Staffel-Editor je Quelle.
const ScalesSubEditor = ({ scales, onChange, disabled, styles, isDarkMode, t }) => {
  const inputSm = `${styles.inputClassName} py-1.5 text-xs`;

  const addScale = () => onChange([...scales, { ...EMPTY_SCALE }]);
  const removeScale = (i) => onChange(scales.filter((_, idx) => idx !== i));
  const updateScale = (i, field, value) =>
    onChange(scales.map((s, idx) => (idx === i ? { ...s, [field]: value } : s)));

  return (
    <div className="mt-2 pl-3 space-y-1.5">
      <p className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
        {t('admin.productForm.sources.scaleTitle')}
      </p>
      {scales.length > 0 && (
        <div className={`grid grid-cols-12 gap-1.5 px-1 text-[10px] font-semibold uppercase tracking-wide ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
          <span className="col-span-3">{t('admin.productForm.sources.scaleMinQty')}</span>
          <span className="col-span-4">{t('admin.productForm.sources.scaleAmount')}</span>
          <span className="col-span-3">{t('admin.productForm.currency')}</span>
          <span className="col-span-2"></span>
        </div>
      )}
      {scales.map((sc, i) => (
        <div key={i} className="grid grid-cols-12 gap-1.5 items-center">
          <div className="col-span-3 relative group/input">
            <div className={styles.inputGlassBackground}></div>
            <input
              type="number"
              value={sc.min_qty}
              onChange={(e) => updateScale(i, 'min_qty', e.target.value)}
              disabled={disabled}
              placeholder="0"
              min="0"
              step="1"
              className={`${inputSm} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
            />
            <div className={styles.focusHighlight}></div>
          </div>
          <div className="col-span-4 relative group/input">
            <div className={styles.inputGlassBackground}></div>
            <input
              type="number"
              value={sc.amount}
              onChange={(e) => updateScale(i, 'amount', e.target.value)}
              disabled={disabled}
              placeholder="0.00"
              min="0"
              step="0.01"
              className={`${inputSm} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
            />
            <div className={styles.focusHighlight}></div>
          </div>
          <div className="col-span-3 relative group/select">
            <div className={styles.inputGlassBackground}></div>
            <select
              value={sc.currency}
              onChange={(e) => updateScale(i, 'currency', e.target.value)}
              disabled={disabled}
              className={`${inputSm} appearance-none cursor-pointer ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {['EUR', 'USD', 'CHF'].map((cur) => (
                <option key={cur} value={cur} className={isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}>
                  {cur}
                </option>
              ))}
            </select>
            <div className={styles.focusHighlight}></div>
          </div>
          <div className="col-span-2 flex justify-center">
            {!disabled && (
              <button
                type="button"
                onClick={() => removeScale(i)}
                title={t('admin.productForm.sources.scaleRemoveRow')}
                className={`p-1 rounded-lg transition-colors ${
                  isDarkMode
                    ? 'text-red-400/70 hover:text-red-400 hover:bg-red-500/10'
                    : 'text-red-500/70 hover:text-red-600 hover:bg-red-50'
                }`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      ))}
      {!disabled && (
        <button
          type="button"
          onClick={addScale}
          className={`flex items-center gap-1 text-xs font-medium px-3 py-1 rounded-lg border transition-colors ${
            isDarkMode
              ? 'border-white/15 text-white/50 hover:text-white/80 hover:border-white/30 hover:bg-white/5'
              : 'border-slate-200 text-slate-400 hover:text-brand hover:border-brand/40 hover:bg-brand/5'
          }`}
        >
          <Plus className="w-3 h-3" />
          {t('admin.productForm.sources.scaleAddRow')}
        </button>
      )}
    </div>
  );
};

// Haupt-Editor: verwaltet ein sources[]-Array fuer interne Produkte.
// Disabled wenn isSapSource — SAP-Produkte haben schreibgeschuetzte Bezugsquellen.
const SourcesEditor = ({ sources, onSourcesChange, disabled = false }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const styles = getGlassStyles(isDarkMode);

  const inputSm = `${styles.inputClassName} py-2 text-sm`;

  const addRow = () => {
    onSourcesChange([...sources, { ...EMPTY_SOURCE, scales: [] }]);
  };

  const removeRow = (idx) => {
    onSourcesChange(sources.filter((_, i) => i !== idx));
  };

  const updateRow = (idx, field, value) => {
    const updated = sources.map((s, i) => {
      if (i !== idx) return s;
      // Bei is_primary: alle anderen auf false setzen (Radio-Semantik).
      if (field === 'is_primary' && value === true) {
        return { ...s, is_primary: true };
      }
      return { ...s, [field]: value };
    });
    // Radio-Semantik: wenn eine Quelle primaer wird, alle anderen deselektieren.
    if (field === 'is_primary' && value === true) {
      onSourcesChange(updated.map((s, i) => (i === idx ? s : { ...s, is_primary: false })));
      return;
    }
    onSourcesChange(updated);
  };

  const updateScales = (idx, newScales) => {
    onSourcesChange(sources.map((s, i) => (i === idx ? { ...s, scales: newScales } : s)));
  };

  return (
    <div className="space-y-4">
      {Array.isArray(sources) && sources.length > 0 ? (
        sources.map((row, idx) => (
          <div
            key={idx}
            className={`rounded-xl p-4 space-y-3 ${
              row.is_primary
                ? isDarkMode
                  ? 'bg-blue-600/10 border border-blue-400/25'
                  : 'bg-brand/5 border border-brand/20'
                : isDarkMode
                ? 'bg-white/5 border border-white/10'
                : 'bg-slate-50 border border-slate-200'
            }`}
          >
            {/* Kopfzeile: Primaer-Markierung + Loeschen */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="source_primary"
                  checked={Boolean(row.is_primary)}
                  onChange={() => updateRow(idx, 'is_primary', true)}
                  disabled={disabled}
                  className={`cursor-pointer ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                />
                <span className={`text-xs font-semibold flex items-center gap-1 ${
                  row.is_primary
                    ? isDarkMode ? 'text-blue-300' : 'text-brand'
                    : isDarkMode ? 'text-white/60' : 'text-slate-500'
                }`}>
                  {row.is_primary && <Star className="w-3 h-3" />}
                  {t('admin.productForm.sources.isPrimary')}
                </span>
              </label>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeRow(idx)}
                  title={t('admin.productForm.sources.removeRow')}
                  className={`p-1.5 rounded-lg transition-colors ${
                    isDarkMode
                      ? 'text-red-400/70 hover:text-red-400 hover:bg-red-500/10'
                      : 'text-red-500/70 hover:text-red-600 hover:bg-red-50'
                  }`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Zeile 1: Lieferant + Name */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
                  {t('admin.productForm.sources.vendor')}
                </label>
                <div className="relative group/input">
                  <div className={styles.inputGlassBackground}></div>
                  <input
                    type="text"
                    value={row.vendor || ''}
                    onChange={(e) => updateRow(idx, 'vendor', e.target.value)}
                    disabled={disabled}
                    placeholder="0007075136"
                    className={`${inputSm} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                  />
                  <div className={styles.focusHighlight}></div>
                </div>
              </div>
              <div>
                <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
                  {t('admin.productForm.sources.vendorName')}
                </label>
                <div className="relative group/input">
                  <div className={styles.inputGlassBackground}></div>
                  <input
                    type="text"
                    value={row.vendor_name || ''}
                    onChange={(e) => updateRow(idx, 'vendor_name', e.target.value)}
                    disabled={disabled}
                    placeholder="Lieferant GmbH"
                    className={`${inputSm} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                  />
                  <div className={styles.focusHighlight}></div>
                </div>
              </div>
            </div>

            {/* Zeile 2: Lief.-Materialnr. */}
            <div>
              <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
                {t('admin.productForm.sources.vendorMaterialNo')}
              </label>
              <div className="relative group/input">
                <div className={styles.inputGlassBackground}></div>
                <input
                  type="text"
                  value={row.vendor_material_no || ''}
                  onChange={(e) => updateRow(idx, 'vendor_material_no', e.target.value)}
                  disabled={disabled}
                  placeholder="500 008 N"
                  className={`${inputSm} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                />
                <div className={styles.focusHighlight}></div>
              </div>
            </div>

            {/* Zeile 3: Preis-Felder */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
                  {t('admin.productForm.sources.netPrice')}
                </label>
                <div className="relative group/input">
                  <div className={styles.inputGlassBackground}></div>
                  <input
                    type="number"
                    value={row.net_price}
                    onChange={(e) => updateRow(idx, 'net_price', e.target.value)}
                    disabled={disabled}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    className={`${inputSm} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                  />
                  <div className={styles.focusHighlight}></div>
                </div>
              </div>
              <div>
                <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
                  {t('admin.productForm.sources.priceUnit')}
                </label>
                <div className="relative group/input">
                  <div className={styles.inputGlassBackground}></div>
                  <input
                    type="number"
                    value={row.price_unit}
                    onChange={(e) => updateRow(idx, 'price_unit', e.target.value)}
                    disabled={disabled}
                    placeholder="1"
                    min="1"
                    step="1"
                    className={`${inputSm} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                  />
                  <div className={styles.focusHighlight}></div>
                </div>
              </div>
              <div>
                <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
                  {t('admin.productForm.sources.orderUnit')}
                </label>
                <div className="relative group/input">
                  <div className={styles.inputGlassBackground}></div>
                  <input
                    type="text"
                    value={row.order_unit || ''}
                    onChange={(e) => updateRow(idx, 'order_unit', e.target.value)}
                    disabled={disabled}
                    placeholder="STK"
                    className={`${inputSm} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                  />
                  <div className={styles.focusHighlight}></div>
                </div>
              </div>
            </div>

            {/* Zeile 4: Lieferzeit */}
            <div>
              <label className={`block text-xs font-semibold mb-1 ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
                {t('admin.productForm.sources.deliveryTime')}
              </label>
              <div className="relative group/input">
                <div className={styles.inputGlassBackground}></div>
                <input
                  type="number"
                  value={row.planned_delivery_time}
                  onChange={(e) => updateRow(idx, 'planned_delivery_time', e.target.value)}
                  disabled={disabled}
                  placeholder="5"
                  min="0"
                  step="1"
                  className={`${inputSm} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                />
                <div className={styles.focusHighlight}></div>
              </div>
            </div>

            {/* Staffeln */}
            <ScalesSubEditor
              scales={Array.isArray(row.scales) ? row.scales : []}
              onChange={(newScales) => updateScales(idx, newScales)}
              disabled={disabled}
              styles={styles}
              isDarkMode={isDarkMode}
              t={t}
            />
          </div>
        ))
      ) : (
        <p className={`text-sm ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
          {t('admin.productForm.sources.empty')}
        </p>
      )}

      {/* Zeile hinzufuegen */}
      {!disabled && (
        <button
          type="button"
          onClick={addRow}
          className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-xl border transition-colors ${
            isDarkMode
              ? 'border-white/20 text-white/70 hover:text-white hover:border-white/40 hover:bg-white/5'
              : 'border-slate-200 text-slate-600 hover:text-brand hover:border-brand/40 hover:bg-brand/5'
          }`}
        >
          <Plus className="w-4 h-4" />
          {t('admin.productForm.sources.addRow')}
        </button>
      )}
    </div>
  );
};

export default SourcesEditor;
